import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/order-status/:orderId — rebuilt in brand primitives. Shows a
 * horizontal 5-step progress tracker, branded order summary, items list,
 * and totals. When the backend `getOrder` endpoint doesn't exist yet, we
 * fall back to a plausible demo order so the screen is useful for design
 * review.
 */

const STEPS = [
  { key: 'submitted', label: 'Submitted',  icon: 'submit' },
  { key: 'confirmed', label: 'Confirmed',  icon: 'check' },
  { key: 'preparing', label: 'Preparing',  icon: 'flame' },
  { key: 'ready',     label: 'Ready',      icon: 'bag' },
  { key: 'completed', label: 'Completed',  icon: 'trophy' },
];

const STATUS_META = {
  pending:   { label: 'Pending',         tone: 'amber' },
  submitted: { label: 'Submitted',       tone: 'blue' },
  confirmed: { label: 'Confirmed',       tone: 'blue' },
  preparing: { label: 'Being prepared',  tone: 'blue' },
  ready:     { label: 'Ready for pickup', tone: 'green' },
  completed: { label: 'Completed',       tone: 'green' },
  cancelled: { label: 'Cancelled',       tone: 'red' },
};

const TONE_COLORS = {
  blue:  { bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  dot: 'var(--cwm-blue-deep)' },
  green: { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', dot: 'var(--cwm-green)' },
  amber: { bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)', dot: 'var(--cwm-amber)' },
  red:   { bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',   dot: 'var(--cwm-skip)' },
};

export default function UnifiedOrderStatus() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { userId, isInitialized } = useContext(UserContext);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isInitialized) return;
    if (!userId) { navigate('/unified'); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await dataService.getOrder(orderId);
        if (!cancelled) setOrder(data);
      } catch {
        // Backend not implemented yet — fall back to a demo order keyed on the
        // URL param so refreshing shows the same order.
        if (!cancelled) setOrder(buildDemoOrder(orderId));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId, userId, isInitialized, navigate]);

  const activeIdx = useMemo(() => {
    if (!order) return 0;
    const i = STEPS.findIndex((s) => s.key === order.status);
    return i >= 0 ? i : 0;
  }, [order]);

  if (loading) return <AppShell backdrop><LoadingHero /></AppShell>;
  if (!order) return <AppShell backdrop><NotFoundHero onBack={() => navigate('/unified/order-history')} /></AppShell>;

  const status = STATUS_META[order.status] || STATUS_META.pending;
  const statusColors = TONE_COLORS[status.tone];

  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '28px 20px 80px', position: 'relative', zIndex: 1 }}>

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, ease: [0.22, 1, 0.36, 1] }}
          style={{ textAlign: 'center', marginBottom: 24 }}
        >
          <div style={{
            width: 84, height: 84, margin: '0 auto 14px', borderRadius: '50%',
            background: 'linear-gradient(160deg, #BCE7FF 0%, #5BC2FF 45%, #1E9BFF 100%)',
            display: 'grid', placeItems: 'center', color: '#fff',
            boxShadow: '0 18px 40px rgba(13,71,161,0.28), inset 0 1px 0 rgba(255,255,255,0.8)',
          }}>
            <CheckIcon size={40} />
          </div>
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 10 }}>
            ORDER #{String(order.id).padStart(5, '0')}
          </div>
          <h1 style={{ fontSize: 34, fontWeight: 900, letterSpacing: '-0.025em', color: 'var(--cwm-ink)', margin: '0 0 8px' }}>
            {order.status === 'completed' ? 'Thanks for dining with us.' : 'Your order is on its way.'}
          </h1>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '6px 14px', borderRadius: 999,
            background: statusColors.bg, color: statusColors.fg,
            fontSize: 12.5, fontWeight: 900, letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: statusColors.dot }} />
            {status.label}
          </div>
        </motion.section>

        {/* Progress tracker */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          className="cwm-sheet" style={{ padding: '22px 18px', marginBottom: 18 }}
        >
          <ProgressTracker activeIdx={activeIdx} />
          {order.eta && order.status !== 'completed' && (
            <div style={{ marginTop: 16, textAlign: 'center', fontSize: 13, fontWeight: 700, color: 'var(--cwm-gray-700)' }}>
              <ClockIcon size={13} /> Estimated ready at <strong style={{ color: 'var(--cwm-ink)' }}>{order.eta}</strong>
            </div>
          )}
        </motion.div>

        {/* Restaurant + method */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
          className="cwm-sheet" style={{ padding: 20, marginBottom: 18, display: 'flex', alignItems: 'center', gap: 14 }}
        >
          <div style={{ width: 48, height: 48, borderRadius: 14, background: 'var(--cwm-blue-pale)', display: 'grid', placeItems: 'center', color: 'var(--cwm-blue-deep)' }}>
            {order.delivery_method === 'delivery' ? <TruckIcon size={22} /> : <BagIcon size={22} />}
          </div>
          <div style={{ flex: 1 }}>
            <div className="cwm-eyebrow" style={{ marginBottom: 2 }}>
              {order.delivery_method === 'delivery' ? 'DELIVERY' : 'PICKUP'}
            </div>
            <div style={{ fontSize: 16, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.005em' }}>
              {order.restaurant_name}
            </div>
            <div style={{ fontSize: 13, color: 'var(--cwm-gray-500)', fontWeight: 600, marginTop: 2 }}>
              {order.restaurant_address}
            </div>
          </div>
        </motion.div>

        {/* Items */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
          className="cwm-sheet" style={{ padding: 0, marginBottom: 18, overflow: 'hidden' }}
        >
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--cwm-gray-100)' }}>
            <div className="cwm-eyebrow" style={{ marginBottom: 2 }}>YOUR ORDER</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--cwm-gray-700)' }}>
              {order.items.length} item{order.items.length === 1 ? '' : 's'}
            </div>
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {order.items.map((item, i) => (
              <li key={i} style={{
                padding: '14px 20px',
                borderBottom: i < order.items.length - 1 ? '1px solid var(--cwm-gray-100)' : 'none',
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 900, color: 'var(--cwm-ink)', marginBottom: 2 }}>
                    {item.qty}× {item.name}
                  </div>
                  {item.note && (
                    <div style={{ fontSize: 12.5, color: 'var(--cwm-gray-500)', fontWeight: 600, marginTop: 2 }}>
                      {item.note}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 14.5, fontWeight: 900, color: 'var(--cwm-ink)', whiteSpace: 'nowrap' }}>
                  ${((item.price_cents * item.qty) / 100).toFixed(2)}
                </div>
              </li>
            ))}
          </ul>

          {/* Totals */}
          <div style={{ padding: 20, background: 'linear-gradient(180deg, #fff, #F6FAFF)' }}>
            <TotalRow label="Subtotal" value={order.subtotal_cents} />
            <TotalRow label="Tax" value={order.tax_cents} />
            {order.delivery_fee_cents > 0 && <TotalRow label="Delivery" value={order.delivery_fee_cents} />}
            {order.tip_cents > 0 && <TotalRow label="Tip" value={order.tip_cents} />}
            <div style={{ height: 1, background: 'var(--cwm-gray-100)', margin: '10px 0' }}/>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 17, fontWeight: 900, color: 'var(--cwm-ink)' }}>
              <span>Total</span>
              <span>${(order.total_cents / 100).toFixed(2)}</span>
            </div>
          </div>
        </motion.div>

        {/* Action row — payment + cancel + navigation. Buttons are
            conditional on order state so we never offer "Pay" on a paid
            order or "Cancel" on a completed one. Pay routes through the
            stub adapter today; Stripe will plug into the same endpoint
            without frontend changes. */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          {order.payment_status === 'unpaid' && order.status !== 'cancelled' && (
            <button
              className="cwm-btn"
              onClick={async () => {
                try {
                  const updated = await dataService.payOrder(order.id);
                  setOrder(updated);
                } catch (e) {
                  // eslint-disable-next-line no-alert
                  window.alert(e.message || 'Payment failed');
                }
              }}
            >
              Pay ${(order.total_cents / 100).toFixed(2)}
            </button>
          )}
          {/* `payment_status` is only present on real orders from the
              backend — demo fallback orders don't set it. That's our
              "is this a real row I can mutate?" gate, so Cancel never
              fires against a synthetic demo id. */}
          {order.payment_status && !['completed', 'cancelled'].includes(order.status) && (
            <button
              className="cwm-btn-secondary"
              onClick={async () => {
                // eslint-disable-next-line no-alert
                if (!window.confirm('Cancel this order?')) return;
                try {
                  const updated = await dataService.cancelOrder(order.id);
                  setOrder(updated);
                } catch (e) {
                  // eslint-disable-next-line no-alert
                  window.alert(e.message || 'Could not cancel');
                }
              }}
            >
              Cancel order
            </button>
          )}
          <button className="cwm-btn-secondary" onClick={() => navigate('/unified/order-history')}>
            Order history
          </button>
          <button className="cwm-btn" onClick={() => navigate('/unified/discover')}>
            Order again
          </button>
        </div>
      </div>
    </AppShell>
  );
}

function ProgressTracker({ activeIdx }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 2, position: 'relative' }}>
      {STEPS.map((step, i) => {
        const done = i < activeIdx;
        const active = i === activeIdx;
        return (
          <React.Fragment key={step.key}>
            <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, width: 72 }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%',
                display: 'grid', placeItems: 'center',
                background: done || active
                  ? 'linear-gradient(160deg, #1E9BFF, #0F7FE8)'
                  : 'var(--cwm-gray-100)',
                color: done || active ? '#fff' : 'var(--cwm-gray-400)',
                boxShadow: active ? '0 8px 20px rgba(15,127,232,0.35)' : 'none',
                transition: 'all 240ms ease',
              }}>
                {done ? <CheckIcon size={18} /> : <StepIcon name={step.icon} size={18} />}
              </div>
              <div style={{
                fontSize: 11,
                fontWeight: done || active ? 900 : 700,
                color: done || active ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-500)',
                textAlign: 'center',
                letterSpacing: '-0.005em',
                lineHeight: 1.2,
              }}>
                {step.label}
              </div>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{
                flex: 1, height: 2, marginTop: 19,
                background: i < activeIdx ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-100)',
                borderRadius: 2,
                transition: 'background 240ms ease',
              }}/>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function TotalRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, fontWeight: 600, color: 'var(--cwm-gray-700)', marginBottom: 6 }}>
      <span>{label}</span>
      <span>${(value / 100).toFixed(2)}</span>
    </div>
  );
}

function LoadingHero() {
  return (
    <div style={{ minHeight: 400, display: 'grid', placeItems: 'center', padding: 40 }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, margin: '0 auto 14px', border: '4px solid var(--cwm-blue-light)', borderTopColor: 'var(--cwm-blue-deep)', borderRadius: '50%', animation: 'cwmSpin 900ms linear infinite' }}/>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--cwm-gray-700)' }}>Loading your order…</div>
        <style>{`@keyframes cwmSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

function NotFoundHero({ onBack }) {
  return (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: '60px 20px', textAlign: 'center' }}>
      <div style={{ width: 64, height: 64, margin: '0 auto 14px', borderRadius: '50%', background: 'var(--cwm-red-light)', display: 'grid', placeItems: 'center', color: 'var(--cwm-red-text)' }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9"/><path d="M12 7v6M12 17h.01"/>
        </svg>
      </div>
      <h1 style={{ fontSize: 26, fontWeight: 900, color: 'var(--cwm-ink)', margin: '0 0 8px' }}>Order not found</h1>
      <p style={{ fontSize: 14, color: 'var(--cwm-gray-500)', marginBottom: 20 }}>
        We couldn't find an order with that ID. It may have been removed or the link is incorrect.
      </p>
      <button className="cwm-btn" onClick={onBack}>View order history</button>
    </div>
  );
}

// ---------- icons ----------
function CheckIcon({ size = 18 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>;
}
function ClockIcon({ size = 13 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>;
}
function TruckIcon({ size = 22 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8h11v9H3zM14 11h4l3 3v3h-7M6 20a2 2 0 100-4 2 2 0 000 4zM18 20a2 2 0 100-4 2 2 0 000 4z"/></svg>;
}
function BagIcon({ size = 22 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 7h12l1 14H5zM9 7a3 3 0 016 0"/></svg>;
}
function StepIcon({ name, size = 18 }) {
  const paths = {
    submit: 'M5 13l4 4L19 7',
    check:  'M5 13l4 4L19 7',
    flame:  'M12 3s4 4 4 8a4 4 0 01-8 0c0-2 1-3 2-4 0 1 0 2 1 2 0-2 1-4 1-6z',
    bag:    'M6 7h12l1 14H5zM9 7a3 3 0 016 0',
    trophy: 'M8 21h8M12 17v4M7 4h10v4a5 5 0 01-10 0zM5 6H3v2a3 3 0 003 3M19 6h2v2a3 3 0 01-3 3',
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d={paths[name] || paths.check}/></svg>;
}

// ---------- demo data ----------
function buildDemoOrder(orderId) {
  return {
    id: Number(orderId) || 1,
    status: 'preparing',
    eta: '7:45pm',
    restaurant_name: 'Il Violino',
    restaurant_address: '180 W 72nd St, New York, NY',
    delivery_method: 'pickup',
    items: [
      { name: 'Gluten-Free Pappardelle', qty: 1, price_cents: 2800, note: 'Extra olive oil, no cheese' },
      { name: 'Vegan Mushroom Risotto',  qty: 1, price_cents: 2400, note: null },
      { name: 'San Pellegrino',          qty: 2, price_cents: 600,  note: null },
    ],
    subtotal_cents: 6400,
    tax_cents: 568,
    delivery_fee_cents: 0,
    tip_cents: 960,
    total_cents: 7928,
    created_at: new Date().toISOString(),
  };
}
